import { useAuth0 } from '@auth0/auth0-react';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import {
  ArrowRightUp,
  Briefcase,
  Burger,
  CheckmarkCircle,
  ChevronSmallDown,
  User,
  WarningCircle,
  X,
} from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { Link } from '@tanstack/react-router';
import { Toaster } from 'react-hot-toast';

import { ButtonClicked, LinkClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useEnterprisePermissions } from 'src/api/queries/user';
import { Button } from 'src/components';
import { DropdownMenu, DropdownMenuItem } from 'src/components/DropdownMenu';
import { RemarkableLogo } from 'src/components/RemarkableLogo';
import { userHelpers } from 'src/utils/userHelpers';

import { Trans, useLingui } from '@lingui/react/macro';
import { TranslateIcon } from '@phosphor-icons/react';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useSideMenuState } from 'src/hooks/useSideMenuState';
import { dynamicActivate, locales } from 'src/i18n';
import { useFeatureFlags } from 'src/utils/useFeatureFlags';
import { HeaderNotification } from './HeaderNotification';

interface HeaderProps {
  variant: 'full' | 'minimal';
  title?: ReactNode;
  isEnterpriseRoot?: boolean;
}

export const Header = ({ variant, title, isEnterpriseRoot }: HeaderProps) => {
  const featureFlags = useFeatureFlags();
  const { t, i18n } = useLingui();
  const auth = useAuth0();
  const sideMenu = useSideMenuState();
  const isRestrictedSamlUser = auth.user
    ? userHelpers.isAccountRestrictedToAccountLinking(auth.user)
    : false;
  const isLoggedInToOrg = auth.user
    ? userHelpers.isLoggedInToOrganization(auth.user)
    : false;
  const enterprisePermissions = useEnterprisePermissions({
    enabled: isLoggedInToOrg,
  });
  const canSeeAdminDashboard =
    isLoggedInToOrg && !!enterprisePermissions.data?.readMembers;

  const showBurgerMenuButton =
    variant === 'full' && auth.isAuthenticated && !isRestrictedSamlUser;

  const orgName = auth.user
    ? userHelpers.getOrganizationDisplayName(auth.user)
    : null;

  return (
    <>
      <div
        data-cy="header"
        className="fixed top-0 z-40 flex h-64 w-full animate-fade-in-down items-center justify-between border-b border-paper-8 bg-paper-2 px-8 text-black lm:sticky lm:z-30 lm:px-20"
      >
        <div className="flex items-center gap-16">
          {showBurgerMenuButton && (
            <Button
              variant="tertiary-neutral"
              onClick={() => {
                tracker.trackEvent(
                  new ButtonClicked({
                    component_location: ComponentLocations.HEADER.MAIN,
                    text: 'Open side menu',
                    action: 'open side menu',
                  })
                );
                sideMenu.toggle();
              }}
              id="open-nav-menu-button"
              data-cy="open-nav-menu"
              aria-label="Open menu"
              className="lm:hidden"
            >
              {sideMenu.isOpen ? <X size={24} /> : <Burger />}
            </Button>
          )}

          {variant === 'full' ? (
            <Link
              to={isEnterpriseRoot ? '/enterprise' : '/'}
              className="flex h-full items-center gap-16 truncate lm:gap-24"
              onClick={(e) => {
                tracker.trackEvent(
                  new LinkClicked({
                    component_location: ComponentLocations.HEADER.MAIN,
                    text: 'reMarkable My Account',
                    link_source: e.currentTarget.href,
                  })
                );
              }}
            >
              <RemarkableLogo
                color="black"
                variant="symbol"
                className={clsx('lxl:hidden', {
                  'ml-12': !showBurgerMenuButton,
                })}
              />
              <RemarkableLogo
                color="black"
                variant="full"
                className="hidden lxl:block"
              />
              <div className="h-32 w-[1px] bg-paper-8"></div>
              <Typography
                as="span"
                variant="body-xl"
                className="!hover:no-underline mt-4 text-muted !no-underline"
              >
                {title || <Trans context="app title">My account</Trans>}
              </Typography>
            </Link>
          ) : (
            <div className="flex w-full justify-between">
              <Link
                to="/"
                className="flex h-full items-center gap-16 truncate lm:gap-24"
                onClick={(e) => {
                  tracker.trackEvent(
                    new LinkClicked({
                      component_location: ComponentLocations.HEADER.MAIN,
                      text: 'reMarkable My Account',
                      link_source: e.currentTarget.href,
                    })
                  );
                }}
              >
                <RemarkableLogo color="black" />
              </Link>

              <div></div>
            </div>
          )}
        </div>

        {variant === 'full' && (
          <div className="flex h-full items-center gap-24">
            <DropdownMenu
              trigger={
                <Button
                  variant="tertiary-neutral"
                  className="group p-12 hover:bg-neutral-light-3 focus:bg-none data-[state=open]:bg-neutral-light-4 lxl:p-16"
                  analytics={{
                    action: 'open header user menu',
                    location: ComponentLocations.HEADER.USER_MENU,
                    text: 'user name',
                  }}
                  data-cy="user-menu"
                >
                  {isEnterpriseRoot ? (
                    <>
                      <Briefcase size={32} />
                      <Typography
                        variant="interface-md"
                        fontWeight="medium"
                        leadingNone
                        className="hidden max-w-112 truncate lxl:block lxl:max-w-[300px]"
                      >
                        {orgName}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <User size={32} />
                      <Typography
                        variant="interface-md"
                        fontWeight="medium"
                        leadingNone
                        className="hidden max-w-112 truncate lxl:block lxl:max-w-[300px]"
                      >
                        {/* For users that haven't set name, name will be their email */}
                        {userHelpers.getName(auth.user)}
                      </Typography>
                    </>
                  )}
                  <ChevronSmallDown
                    size={24}
                    className="transition-all group-data-[state=open]:rotate-180"
                  />
                </Button>
              }
              side="bottom"
              align="end"
              sideOffset={12}
            >
              <Dropdown.Label className="rounded-sm px-[14px] pt-16 pb-8 interface-md text-14">
                {auth.user?.email}
              </Dropdown.Label>

              {featureFlags.EnableLocalization && (
                <DropdownMenuItem asChild>
                  <Dropdown.DropdownMenuSub>
                    <Dropdown.DropdownMenuSubTrigger className="cursor-pointer px-16 py-8 text-14 !outline-hidden focus:bg-neutral-light-3">
                      <TranslateIcon size={16} className="mr-8 inline" />
                      <span>
                        {locales[i18n.locale as keyof typeof locales] ||
                          t`Language`}
                      </span>
                    </Dropdown.DropdownMenuSubTrigger>

                    <Dropdown.DropdownMenuSubContent className="w-144 bg-white p-0 py-4 shadow-2xl">
                      {Object.entries(locales).map(
                        ([localeKey, localeName]) => (
                          <DropdownMenuItem asChild key={localeKey}>
                            <button
                              className={clsx(
                                'w-full px-16 py-8 text-left !outline-hidden focus:bg-neutral-light-3',
                                localeKey === i18n.locale &&
                                  'bg-neutral-light-3 font-medium'
                              )}
                              onClick={() => {
                                void dynamicActivate(localeKey).then(
                                  (newLocale) => {
                                    window.localStorage.setItem(
                                      'i18nLocale',
                                      newLocale
                                    );
                                  }
                                );

                                tracker.trackEvent(
                                  new ButtonClicked({
                                    component_location:
                                      ComponentLocations.HEADER.USER_MENU,
                                    text: `Change language to ${localeName}`,
                                    action: 'change language',
                                  })
                                );
                              }}
                            >
                              {localeName}
                            </button>
                          </DropdownMenuItem>
                        )
                      )}
                    </Dropdown.DropdownMenuSubContent>
                  </Dropdown.DropdownMenuSub>
                </DropdownMenuItem>
              )}

              <Dropdown.Separator className="mx-12 my-4 h-[1px] bg-neutral-light-4" />

              {!isRestrictedSamlUser && isEnterpriseRoot && (
                <DropdownMenuItem asChild>
                  <Link
                    to="/"
                    target={
                      featureFlags.EnableEnterpriseInNewTab
                        ? '_blank'
                        : undefined
                    }
                    className="!outline-hidden focus:bg-neutral-light-3"
                    onClick={() => {
                      tracker.trackEvent(
                        new ButtonClicked({
                          component_location:
                            ComponentLocations.HEADER.USER_MENU,
                          text: t`My account`,
                          action: 'click',
                        })
                      );
                    }}
                  >
                    <span className="flex items-center gap-8">
                      <Trans>My account</Trans>
                      <ArrowRightUp className="shrink-0" size={16} />
                    </span>
                  </Link>
                </DropdownMenuItem>
              )}

              {canSeeAdminDashboard && !isEnterpriseRoot && (
                <DropdownMenuItem asChild>
                  <Link
                    to="/enterprise"
                    className="!outline-hidden focus:bg-neutral-light-3"
                    target={
                      featureFlags.EnableEnterpriseInNewTab
                        ? '_blank'
                        : undefined
                    }
                    onClick={() => {
                      tracker.trackEvent(
                        new ButtonClicked({
                          component_location:
                            ComponentLocations.HEADER.USER_MENU,
                          text: t`Enterprise`,
                          action: 'click',
                        })
                      );
                    }}
                  >
                    <span className="flex items-center gap-8">
                      <Trans>Admin dashboard</Trans>{' '}
                      <ArrowRightUp className="shrink-0" size={16} />
                    </span>{' '}
                  </Link>
                </DropdownMenuItem>
              )}

              <DropdownMenuItem asChild>
                <Link
                  to="/logout"
                  className="!outline-hidden focus:bg-neutral-light-3"
                  data-cy="nav-log-out"
                  onClick={() => {
                    tracker.trackEvent(
                      new ButtonClicked({
                        component_location: ComponentLocations.HEADER.USER_MENU,
                        text: t`Log out`,
                        action: 'logout',
                      })
                    );
                  }}
                >
                  <Trans>Log out</Trans>
                </Link>
              </DropdownMenuItem>
            </DropdownMenu>
          </div>
        )}
      </div>
      {variant === 'full' && (
        <div className="w-full">
          <HeaderNotification />
        </div>
      )}
      <Toaster
        position="top-right"
        containerStyle={{ top: 72, zIndex: 49 }}
        toastOptions={{
          style: {
            borderRadius: '4px',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingTop: '12px',
            paddingBottom: '12px',
            alignItems: 'center',
          },
          error: {
            icon: (
              <WarningCircle
                size={24}
                className="shrink-0 text-feedback-red-500"
              />
            ),
          },
          success: {
            icon: (
              <CheckmarkCircle
                size={24}
                className="shrink-0 text-feedback-green-500"
              />
            ),
          },
        }}
      />
    </>
  );
};
